import SideNavigation, { SideNavigationProps } from '@amzn/awsui-components-core/side-navigation';
import { buildEmbeddedAppPath } from 'src/utilities/urlName';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { logger } from 'src/logger';
import { ENVIRONMENT_VARIABLES } from '../../constants/EnvironmentConstants';
import { getFinSuiteNavigationItems, getNavigationItemsBySection, ROUTE_CONFIG } from '../../utilities/RouteConfig';
import { ApplicationFlatTableEntity } from '../context/AppContextModels';
import { useAppContext } from '../context/AppContextProvider';
import { useAuth } from '../context/AuthContextProvider';
import { UserActionsEventTypes } from '../das-finsuite/das-finsuite-constants';
import { FinTechOpsHref } from '../fintech-ops/FinTechOpsConstants';
import { ROUTE_PRELOAD_MAP } from '../fintech-ops/shared/lazyRoutes';

import './HomePageSideNavigationV2.scss';

/**
 * Builds the full SideNavigationProps.Item[] for the Cloudscape SideNavigation component.
 */
const buildNavigationItems = (
  userLDAPGroups: string[] | undefined,
  stage: string,
  homePageContent: ReturnType<typeof useAppContext>['homePageContent'],
  favoriteApps: ApplicationFlatTableEntity[]
): SideNavigationProps.Item[] => {
  const items: SideNavigationProps.Item[] = [];

  // section-group only accepts Link | Section | LinkGroup | ExpandableLinkGroup (not Divider)
  const linksOnly = (arr: SideNavigationProps.Item[]): SideNavigationProps.Link[] =>
    arr.filter((i): i is SideNavigationProps.Link => i.type === 'link');

  // FinSuite top-level links (View All Apps, Product Launches, etc.)
  items.push(...getFinSuiteNavigationItems(userLDAPGroups, stage));

  // Org Tools section (includes Operational Excellence as a single link).
  // OE is always visible — it's open to all users — so this section always renders.
  const orgToolsItems = linksOnly(getNavigationItemsBySection('org-tools', userLDAPGroups, stage));
  const oeConfig = ROUTE_CONFIG['/operational-excellence'];
  const allOrgToolsItems: SideNavigationProps.Link[] = [
    ...orgToolsItems,
    { type: 'link', text: oeConfig.navigationText || oeConfig.breadcrumbText, href: FinTechOpsHref.finTechOps }
  ];
  items.push({ type: 'divider' }, { type: 'section-group', title: 'Org Tools', items: allOrgToolsItems });

  // Kingpin section — accessibleTo on routes handles group filtering
  const kingpinItems = linksOnly(getNavigationItemsBySection('kingpin', userLDAPGroups, stage));
  if (kingpinItems.length > 0) {
    items.push({ type: 'divider' }, { type: 'section-group', title: 'Kingpin', items: kingpinItems });
  }

  // My Favorites section
  if (favoriteApps.length > 0) {
    items.push(
      { type: 'divider' },
      {
        type: 'section-group',
        title: 'My Favorites',
        items: favoriteApps.map((app) => ({
          type: 'link' as const,
          text: app.applicationName,
          href: app.embeddingEnabled ? buildEmbeddedAppPath(app) : app.applicationLink || '#',
          external: !app.embeddingEnabled && !!app.applicationLink
        }))
      }
    );
  }

  return items;
};

export const HomePageSideNavigationV2: React.FC = () => {
  const { pathname, hash } = useLocation();
  const navigate = useNavigate();
  const userAuthenticationDetails = useAuth();
  const { favoriteApplications, parsedApplications, homePageContent } = useAppContext();
  const [activeHref, setActiveHref] = useState('');
  const navRef = useRef<HTMLDivElement>(null);

  const favoriteAppsList = useMemo<ApplicationFlatTableEntity[]>(() => {
    const activeParsedApps = parsedApplications.filter((app) => app.isActive);
    return activeParsedApps.filter((app) => favoriteApplications.includes(app.id));
  }, [favoriteApplications, parsedApplications]);

  const items = useMemo(
    () => buildNavigationItems(userAuthenticationDetails.userLDAPGroups, ENVIRONMENT_VARIABLES.env.Stage, homePageContent, favoriteAppsList),
    [userAuthenticationDetails.userLDAPGroups, homePageContent, favoriteAppsList]
  );

  // Collect all internal hrefs from the nav tree (walks section-groups recursively).
  const allHrefs = useMemo(() => {
    const out: string[] = [];
    const walk = (arr: ReadonlyArray<SideNavigationProps.Item>) => {
      arr.forEach((it) => {
        if (it.type === 'link' && it.href && it.href.startsWith('/')) out.push(it.href);
        else if ('items' in it && Array.isArray(it.items)) walk(it.items);
      });
    };
    walk(items);
    return out;
  }, [items]);

  useEffect(() => {
    // Tab-level routes append a trailing slug (e.g. `/operational-excellence/ticket-health/details`).
    // Resolve activeHref to the longest nav href that is a prefix of the current pathname.
    const match = allHrefs.filter((h) => pathname === h || pathname.startsWith(h + '/')).sort((a, b) => b.length - a.length)[0];
    setActiveHref((match ?? pathname) + hash);
  }, [pathname, hash, allHrefs]);

  const handleFollow: SideNavigationProps['onFollow'] = (event) => {
    event.preventDefault();
    const { href, text, external } = event.detail;

    logger.info('DaS FinSuite -> User Action: FinSuite Home page side nav', {
      event_type: UserActionsEventTypes.HOME_PAGE_SIDE_NAV_CLICK,
      value: text
    });

    if (external || href.startsWith('http://') || href.startsWith('https://')) {
      window.open(href, '_blank', 'noopener,noreferrer');
      return;
    }

    navigate(href, {
      state: { from: '/', redirectFromHomePage: true, fromAppName: '', fromAppId: '' }
    });
  };

  // Preload lazy route chunks on nav-link hover (eliminates Suspense flash on click).
  // Empty deps is intentional: navRef is stable and ROUTE_PRELOAD_MAP is module-scoped,
  // so listeners only need to bind once at mount.
  useEffect(() => {
    const root = navRef.current;
    if (!root) return;
    const preloaded = new Set<string>();
    const onEnter = (e: Event) => {
      const a = (e.target as HTMLElement).closest('a[href]') as HTMLAnchorElement | null;
      if (!a) return;
      const href = a.getAttribute('href') || '';
      if (preloaded.has(href)) return;
      const preload = ROUTE_PRELOAD_MAP[href];
      if (preload) {
        preloaded.add(href);
        preload().catch(() => preloaded.delete(href));
      }
    };
    root.addEventListener('mouseover', onEnter, { passive: true });
    root.addEventListener('focusin', onEnter);
    return () => {
      root.removeEventListener('mouseover', onEnter);
      root.removeEventListener('focusin', onEnter);
    };
  }, []);

  return (
    <div className="compact-side-nav" ref={navRef}>
      <SideNavigation activeHref={activeHref} items={items} onFollow={handleFollow} />
    </div>
  );
};
